import React, {Component} from 'react';
import ContentWrapper from '../Layout/ContentWrapper';
import {Card, CardBody, Col, Row, Input, Button, FormGroup} from 'reactstrap';
import Swal from 'sweetalert2'
import axios from 'axios';
import moment from 'moment';
import 'moment-timezone';

// Convert unix timestamp (seconds) to Atlantic time (America/Halifax) "HH:MM" for <input type="time">
function todayHalifax() {
    return moment().tz('America/Halifax').format('YYYY-MM-DD');
}

function currentTimeHalifax() {
    return moment().tz('America/Halifax').format('HH:mm');
}

// localStorage key used to persist an in-progress trip sheet draft
// so drivers can close/background the app and resume later.
const DRAFT_KEY = 'gh_driver_trip_draft';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function unixToTime(ts) {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    if (isNaN(d.getTime())) return '';
    try {
        const formatted = new Intl.DateTimeFormat('en-CA', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'America/Halifax',
        }).format(d);
        // Some environments may still include locale-specific markers; normalize to HH:MM
        const m = formatted.match(/(\d{1,2}):(\d{2})/);
        if (!m) return '';
        const hh = m[1].padStart(2, '0');
        const mm = m[2];
        return `${hh}:${mm}`;
    } catch (e) {
        // Fallback to local 24h time if Intl/timeZone not available
        return d.toISOString().slice(11, 16);
    }
}

class TripsCreate extends Component {
    constructor(props) {
        super(props);
        this.state = {
            date: todayHalifax(),
            truck_id: '',
            comments: '',
            fuel_amount: '',
            fuel_litres: '',
            def_amount: '',
            def_litres: '',
            total_distance: '',
            total_hours: '',
            trip_id: null,
            is_submitted: false,
            attachments: [],
            rows: [
                {container: '', chassis: '', location: '', arrive: '', depart: '', odometer: '', delay: ''}
            ],
            trucks: []
        };
        this.saveTimeout = null;
    }

    // Persist the current form state to localStorage so drivers can
    // close/background the app and resume later.
    saveToLocalStorage() {
        if (this.state && this.state.is_submitted) return;
        const draft = {
            date: this.state.date,
            truck_id: this.state.truck_id,
            comments: this.state.comments,
            fuel_amount: this.state.fuel_amount,
            fuel_litres: this.state.fuel_litres,
            def_amount: this.state.def_amount,
            def_litres: this.state.def_litres,
            total_distance: this.state.total_distance,
            total_hours: this.state.total_hours,
            rows: this.state.rows,
            trip_id: this.state.trip_id,
            savedAt: Date.now()
        };
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        } catch (e) {
            // ignore (private mode or storage full)
        }
    }

    loadFromLocalStorage() {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (!raw) return null;
            const draft = JSON.parse(raw);
            if (!draft || !draft.savedAt) return null;
            if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
                localStorage.removeItem(DRAFT_KEY);
                return null;
            }
            return draft;
        } catch (e) {
            return null;
        }
    }

    clearLocalStorage() {
        try {
            localStorage.removeItem(DRAFT_KEY);
        } catch (e) {
            // ignore
        }
    }

    updateUrlWithTripId(trip_id) {
        try {
            const params = new URLSearchParams(window.location.search || '');
            params.set('trip_id', trip_id);
            const newUrl = window.location.pathname + '?' + params.toString();
            window.history.replaceState({}, '', newUrl);
        } catch (e) {
            // ignore
        }
    }

    componentDidMount() {
        document.title = "Create Trip | Greenhaven Transportation";
        var self = this;
        axios.get(`/api/driver/trucks`)
            .then(function (response) {
                self.setState({trucks: response.data.trucks || []});
            })
            .catch(function (error) {
                Swal("Error", (error.response && error.response.data && (error.response.data.message || error.response.data.Message)) || 'Failed to load trucks', "error");
            });
        // If URL has a trip_id (either as /trips/:trip_id or ?trip_id=...), load existing trip; otherwise load initial defaults then autosave as a new trip
        const params = new URLSearchParams(window.location.search || '');
        const paramTripId = params.get('trip_id');
        const urlTripId = this.props.match && this.props.match.params && this.props.match.params.trip_id;
        const existingTripId = urlTripId || paramTripId;

        if (existingTripId) {
            axios.get(`/api/tripsheet/${existingTripId}`)
                .then(function (response) {
                    if (!response.data || !response.data.trip) return;
                    const trip = response.data.trip;
                    const rows = (response.data.rows || []).map(function (r) {
                        return {
                            container: r.container || '',
                            chassis: r.chassis || '',
                            location: r.location || '',
                            arrive: r.arrive || '',
                            depart: r.depart || '',
                            odometer: r.odometer || '',
                            delay: r.delay || ''
                        };
                    });
                    const total_distance = trip.total_distance || '';
                    const total_hours = trip.total_hours || '';
                    self.setState({
                        trip_id: trip.trip_id,
                        date: trip.date || self.state.date,
                        truck_id: trip.truck_id ? String(trip.truck_id) : self.state.truck_id,
                        comments: trip.comments || '',
                        fuel_amount: trip.fuel_amount || '',
                        fuel_litres: trip.fuel_litres || '',
                        def_amount: trip.def_amount || '',
                        def_litres: trip.def_litres || '',
                        total_distance,
                        total_hours,
                        rows: rows.length ? rows : self.state.rows,
                        is_submitted: trip.status === 'complete'
                    }, function () {
                        self.saveToLocalStorage();
                        if (self.state.trip_id) {
                            self.updateUrlWithTripId(self.state.trip_id);
                        }
                    });
                    // Load attachments for this trip
                    axios.get(`/api/tripsheet/${trip.trip_id}/attachments`)
                        .then(function (resp2) {
                            self.setState({attachments: (resp2.data && resp2.data.attachments) || []});
                        })
                        .catch(function () {
                        });
                })
                .catch(function (error) {
                    if (error && error.response && error.response.status === 401) {
                        if (self.props.userHasAuthenticated) {
                            self.props.userHasAuthenticated(false);
                        }
                        if (self.props.history) {
                            self.props.history.push('/login');
                            return;
                        }
                    }
                    // If load fails, fall back to a blank/initial sheet
                });
        } else {
            // Try to resume an in-progress draft first; otherwise load initial defaults.
            const draft = self.loadFromLocalStorage();
            if (draft) {
                self.setState({
                    trip_id: draft.trip_id || null,
                    date: draft.date || self.state.date,
                    truck_id: draft.truck_id || '',
                    comments: draft.comments || '',
                    fuel_amount: draft.fuel_amount || '',
                    fuel_litres: draft.fuel_litres || '',
                    def_amount: draft.def_amount || '',
                    def_litres: draft.def_litres || '',
                    total_distance: draft.total_distance || '',
                    total_hours: draft.total_hours || '',
                    rows: Array.isArray(draft.rows) && draft.rows.length ? draft.rows : self.state.rows,
                    is_submitted: false
                }, function () {
                    self.saveToLocalStorage();
                    if (self.state.trip_id) {
                        self.updateUrlWithTripId(self.state.trip_id);
                        self.loadAttachments();
                    }
                    self.autosaveNow();
                });
                return;
            }

            // Load initial trip sheet defaults (equipment + suggested trip legs)
            axios.post(`/api/tripsheet/initial`, {})
                .then(function (response) {
                    if (!response.data || !response.data.initial_data) return;
                    const initial = response.data.initial_data;

                    // Pre-select truck if equipment_id was detected
                    if (initial.equipment_id) {
                        self.setState({truck_id: String(initial.equipment_id)});
                    }

                    // Build rows from suggested trips; server decides which stops have arrive/depart
                    if (Array.isArray(initial.trips) && initial.trips.length > 0) {
                        const rows = initial.trips.map(function (t) {
                            return {
                                container: '',
                                chassis: '',
                                location: t.location_name || '',
                                arrive: unixToTime(t.arrive_time),
                                depart: unixToTime(t.depart_time),
                                odometer: typeof t.odometer === 'number' && !isNaN(t.odometer)
                                    ? String(Math.round(t.odometer))
                                    : (t.odometer || ''),
                                delay: ''
                            };
                        });

                        // also compute totals from initial rows
                        const total_distance = self.computeTotalDistance(rows);
                        const total_hours = self.computeTotalHours(rows);
                        self.setState({rows: rows, total_distance, total_hours}, function () {
                            // Autosave immediately to create a new trip and update URL with trip_id
                            self.autosaveNow();
                        });
                    } else {
                        // No suggested trips; still autosave a blank trip so we get a trip_id
                        self.autosaveNow();
                    }
                })
                .catch(function (error) {
                    if (error && error.response && error.response.status === 401) {
                        if (self.props.userHasAuthenticated) {
                            self.props.userHasAuthenticated(false);
                        }
                        if (self.props.history) {
                            self.props.history.push('/login');
                            return;
                        }
                    }
                    // Non-fatal for other errors: driver can still fill sheet manually
                    self.autosaveNow();
                });
        }
    }

    // Clear the current trip sheet: reset fields and rows, and keep today's date.
    clearTrip = () => {
        const today = todayHalifax();
        this.setState({
            date: today,
            truck_id: '',
            comments: '',
            fuel_amount: '',
            fuel_litres: '',
            def_amount: '',
            def_litres: '',
            total_distance: '',
            total_hours: '',
            rows: [
                {container: '', chassis: '', location: '', arrive: '', depart: '', odometer: '', delay: ''}
            ]
        }, () => {
            this.scheduleAutosave();
        });
    }

    componentWillUnmount() {
        this.saveToLocalStorage();
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
    }

    setField = (name, value) => {
        if (this.state.is_submitted) return;
        this.setState({[name]: value}, () => {
            this.scheduleAutosave();
        });
    }

    computeTotalDistance(rows) {
        let total = 0;
        let prevOdo = null;
        for (let i = 0; i < rows.length; i++) {
            const odo = rows[i].odometer !== '' && rows[i].odometer !== null ? parseFloat(rows[i].odometer) : null;
            if (prevOdo !== null && odo !== null && odo > prevOdo) {
                total += (odo - prevOdo);
            }
            prevOdo = odo !== null ? odo : prevOdo;
        }
        if (!isFinite(total) || total <= 0) return '';
        return String(Math.round(total * 10) / 10);
    }

    // Total hours worked: continuous span from the earliest to the latest event
    // across all rows. This includes both on-duty (arrive->depart at a stop) and
    // driving time (gaps between rows). Handles overnight trips where a later
    // time is earlier in the day (e.g. 23:00 -> 01:00). Editable by user.
    computeTotalHours(rows) {
        const toMinutes = (t) => {
            const parts = String(t).split(':');
            const h = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10);
            if (isNaN(h) || isNaN(m)) return null;
            return h * 60 + m;
        };

        const events = [];
        let last = null;
        for (let i = 0; i < rows.length; i++) {
            const arrive = rows[i].arrive;
            const depart = rows[i].depart;
            if (!arrive && !depart) continue;

            const arriveMin = arrive ? toMinutes(arrive) : null;
            const departMin = depart ? toMinutes(depart) : null;
            if (arriveMin === null && departMin === null) continue;

            // Process the row's events in order: arrive, then depart.
            // If a time is earlier than the previous event, it has wrapped past
            // midnight, so add 24 hours.
            if (arriveMin !== null) {
                let m = arriveMin;
                if (last !== null && m < last) m += 24 * 60;
                events.push(m);
                last = m;
            }
            if (departMin !== null) {
                let m = departMin;
                const prev = arriveMin !== null ? events[events.length - 1] : last;
                if (prev !== null && m < prev) m += 24 * 60;
                events.push(m);
                last = m;
            }
        }

        if (events.length < 2) return '';
        const totalMin = events[events.length - 1] - events[0];
        if (totalMin <= 0) return '';
        const hours = totalMin / 60;
        return String(Math.round(hours * 10) / 10);
    }

    setRowField = (index, name, value) => {
        if (this.state.is_submitted) return;
        const rows = [...this.state.rows];
        rows[index] = {...rows[index], [name]: value};
        // normalize odometer to whole km when editing that field
        if (name === 'odometer') {
            const num = value !== '' ? parseFloat(value) : null;
            rows[index].odometer = (num !== null && !isNaN(num)) ? String(Math.round(num)) : '';
        }
        const total_distance = this.computeTotalDistance(rows);
        const total_hours = this.computeTotalHours(rows);
        this.setState({rows, total_distance, total_hours}, () => {
            this.scheduleAutosave();
        });
    }

    addRow = () => {
        if (this.state.is_submitted) return;
        const existing = this.state.rows || [];
        const lastRow = existing.length > 0 ? existing[existing.length - 1] : {};
        const newRow = {
            container: lastRow.container || '',
            chassis: lastRow.chassis || '',
            location: '',
            arrive: '',
            depart: '',
            odometer: '',
            delay: ''
        };
        const rows = [...existing, newRow];
        const total_distance = this.computeTotalDistance(rows);
        const total_hours = this.computeTotalHours(rows);
        this.setState({rows, total_distance, total_hours}, () => {
            this.scheduleAutosave();
        });
    }

    removeRow = (index) => {
        if (this.state.is_submitted) return;
        const rows = this.state.rows.filter((_, i) => i !== index);
        const total_distance = this.computeTotalDistance(rows);
        const total_hours = this.computeTotalHours(rows);
        this.setState({rows, total_distance, total_hours}, () => {
            this.scheduleAutosave();
        });
    }

    // If a time field is blank when focused, initialize it to the current Atlantic time (HH:MM)
    ensureTimeValue = (index, field) => {
        if (this.state.is_submitted) return;
        const rows = this.state.rows || [];
        if (!rows[index] || rows[index][field]) return;
        this.setRowField(index, field, currentTimeHalifax());
    }

    // Build payload matching backend trip sheet schema
    buildPayload() {
        return {
            date: this.state.date,
            truck_id: this.state.truck_id ? parseInt(this.state.truck_id, 10) : null,
            comments: this.state.comments || '',
            fuel_amount: this.state.fuel_amount || null,
            fuel_litres: this.state.fuel_litres || null,
            def_amount: this.state.def_amount || null,
            def_litres: this.state.def_litres || null,
            total_distance: this.state.total_distance || null,
            total_hours: this.state.total_hours || null,
            rows: this.state.rows.map((r, idx) => ({
                seq: idx + 1,
                container: r.container || '',
                chassis: r.chassis || '',
                location: r.location || '',
                arrive: r.arrive || '',
                depart: r.depart || '',
                odometer: r.odometer !== '' && r.odometer !== null ? parseInt(r.odometer, 10) : null,
                delay: r.delay || ''
            }))
        };
    }

    autosaveNow = () => {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
            this.saveTimeout = null;
        }
        this.saveToLocalStorage();
        const payload = this.buildPayload();
        const self = this;

        // If we already have a trip_id, update; otherwise create a new one
        if (this.state.trip_id) {
            axios.put(`/api/tripsheet/${this.state.trip_id}`, payload)
                .then(function () {
                    // no-op on success
                })
                .catch(function (error) {
                    if (error && error.response && error.response.status === 401) {
                        if (self.props.userHasAuthenticated) {
                            self.props.userHasAuthenticated(false);
                        }
                        if (self.props.history) {
                            self.props.history.push('/login');
                            return;
                        }
                    }
                    // swallow other autosave errors; driver can still continue
                });
        } else {
            axios.post(`/api/tripsheet`, payload)
                .then(function (response) {
                    if (!response.data || !response.data.trip_id) return;
                    const trip_id = response.data.trip_id;
                    self.setState({trip_id}, function () {
                        self.saveToLocalStorage();
                    });
                    try {
                        const params = new URLSearchParams(window.location.search || '');
                        params.set('trip_id', trip_id);
                        const newUrl = window.location.pathname + '?' + params.toString();
                        window.history.replaceState({}, '', newUrl);
                    } catch (e) {
                        // ignore URL errors
                    }
                })
                .catch(function (error) {
                    if (error && error.response && error.response.status === 401) {
                        if (self.props.userHasAuthenticated) {
                            self.props.userHasAuthenticated(false);
                        }
                        if (self.props.history) {
                            self.props.history.push('/login');
                            return;
                        }
                    }
                    // swallow other autosave errors
                });
        }
    }

    scheduleAutosave() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        // Persist to localStorage instantly so the draft survives app kills.
        this.saveToLocalStorage();
        this.saveTimeout = setTimeout(() => this.autosaveNow(), 1500);
    }

    submitTrip = () => {
        const self = this;
        if (!this.state.trip_id) {
            // Ensure we have a trip_id before final submit
            this.autosaveNow();
        }

        // Use the same SweetAlert2 invocation style as elsewhere in the app
        Swal({
            title: 'Submit Trip Sheet?',
            text: 'Are you sure you want to submit this trip sheet? This trip sheet will not be able to be edited.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, submit',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            // Support both older (result.value) and newer (result.isConfirmed) SweetAlert2 APIs
            const confirmed = (result && (result.isConfirmed || result.value));
            if (!confirmed) return;

            const payload = self.buildPayload();
            payload.status = 'complete';

            axios.put(`/api/tripsheet/${self.state.trip_id}`, payload)
                .then(function () {
                    self.clearLocalStorage();
                    self.setState({is_submitted: true});
                    // After submit, navigate to the canonical view URL /trips/:trip_id
                    try {
                        if (self.props.history && self.state.trip_id) {
                            self.props.history.push(`/trips/${self.state.trip_id}`);
                        }
                    } catch (e) {
                    }
                    Swal.fire('Submitted', 'Trip sheet has been submitted.', 'success');
                })
                .catch(function () {
                    Swal.fire('Error', 'Failed to submit trip sheet. Please try again.', 'error');
                });
        });
    }

    startNewTrip = () => {
        this.clearLocalStorage();
        const today = todayHalifax();
        // Reset to a fresh draft and clear trip_id so autosave creates a new sheet
        this.setState({
            date: today,
            truck_id: '',
            comments: '',
            fuel_amount: '',
            fuel_litres: '',
            def_amount: '',
            def_litres: '',
            total_distance: '',
            total_hours: '',
            trip_id: null,
            is_submitted: false,
            rows: [
                {container: '', chassis: '', location: '', arrive: '', depart: '', odometer: '', delay: ''}
            ]
        }, () => {
            // Clear trip_id from URL; autosave will create a new one
            try {
                const params = new URLSearchParams(window.location.search || '');
                params.delete('trip_id');
                const newUrl = window.location.pathname + (params.toString() ? ('?' + params.toString()) : '');
                window.history.replaceState({}, '', newUrl);
            } catch (e) {
            }
            this.autosaveNow();
        });
    }

    // Attachments helpers
    loadAttachments = () => {
        const self = this;
        if (!this.state.trip_id) return;
        axios.get(`/api/tripsheet/${this.state.trip_id}/attachments`)
            .then(function (resp) {
                self.setState({attachments: (resp.data && resp.data.attachments) || []});
            })
            .catch(function () {
            });
    }

    handleAttachmentChange = (e) => {
        const self = this;
        const files = e.target.files;
        if (!files || files.length === 0 || !this.state.trip_id || this.state.is_submitted) return;

        const allowed = ['application/pdf', 'image/jpeg', 'image/jpg'];

        Array.from(files).forEach(function (file) {
            if (!allowed.includes(file.type)) {
                Swal('Invalid file', 'Only PDF and JPG/JPEG files are allowed.', 'error');
                return;
            }
            const formData = new FormData();
            formData.append('file', file);

            axios.post(`/api/tripsheet/${self.state.trip_id}/attachments`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            }).then(function (resp) {
                self.loadAttachments();

                const data = resp && resp.data ? resp.data : {};
                const container_number = data.container_number;
                if (container_number) {
                    Swal({
                        title: 'Container number identified',
                        text: 'Container number "' + container_number + '" was identified.',
                        type: 'info',
                        showCancelButton: true,
                        confirmButtonText: 'Apply to Trip',
                        cancelButtonText: 'Ignore'
                    }).then((result) => {
                        const confirmed = (result && (result.isConfirmed || result.value));
                        if (!confirmed) return;
                        self.applyContainerNumberToTrip(container_number);
                    });
                }
            }).catch(function (error) {
                if (error && error.response && error.response.status === 401) {
                    if (self.props.userHasAuthenticated) {
                        self.props.userHasAuthenticated(false);
                    }
                    if (self.props.history) {
                        self.props.history.push('/login');
                        return;
                    }
                }
                Swal('Error', 'Failed to upload attachment.', 'error');
            });
        });

        // reset input so the same file can be selected again if needed
        e.target.value = '';
    }

    applyContainerNumberToTrip = (container_number) => {
        if (!container_number) return;
        if (this.state.is_submitted) return;

        const rows = [...this.state.rows];
        if (!rows.length) return;

        let targetIndex = rows.findIndex((r) => !r.container || String(r.container).trim() === '');
        if (targetIndex === -1) {
            targetIndex = 0;
        }

        rows[targetIndex] = {
            ...rows[targetIndex],
            container: container_number
        };

        this.setState({rows}, () => {
            this.scheduleAutosave();
        });
    }

    deleteAttachment = (attachment_id) => {
        const self = this;
        if (!this.state.trip_id || this.state.is_submitted) return;
        axios.delete(`/api/tripsheet/${this.state.trip_id}/attachments/${attachment_id}`)
            .then(function () {
                self.loadAttachments();
            })
            .catch(function (error) {
                if (error && error.response && error.response.status === 401) {
                    if (self.props.userHasAuthenticated) {
                        self.props.userHasAuthenticated(false);
                    }
                    if (self.props.history) {
                        self.props.history.push('/login');
                        return;
                    }
                }
                Swal('Error', 'Failed to delete attachment.', 'error');
            });
    }

    render() {
        return (
            <ContentWrapper>
                <div className="content-heading d-flex justify-content-between align-items-center">
                    <div>New Trip Sheet</div>
                    {!this.state.is_submitted && (
                        <Button color="secondary" size="sm" onClick={this.clearTrip}>Clear</Button>
                    )}
                </div>
                <Card className="card card-default mx-0">
                    <CardBody>
                        <form className="form-horizontal">
                            <Row>
                                <Col sm="12" md="4">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Date</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.date}</div>
                                        ) : (
                                            <Input
                                                type="date"
                                                bsSize="sm"
                                                style={{maxWidth: '100%'}}
                                                value={this.state.date}
                                                onChange={(e) => this.setField('date', e.target.value)}
                                            />
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="4">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Truck</label>
                                        {this.state.is_submitted ? (
                                            <div>{(this.state.trucks.find(t => String(t.equipment_id) === String(this.state.truck_id)) || {}).name || ''}</div>
                                        ) : (
                                            <Input type="select" value={this.state.truck_id}
                                                   onChange={(e) => this.setField('truck_id', e.target.value)}>
                                                <option value="">(select)</option>
                                                {this.state.trucks.map((t) => (
                                                    <option key={t.equipment_id} value={t.equipment_id}>{t.name}</option>
                                                ))}
                                            </Input>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="4">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Comments</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.comments}</div>
                                        ) : (
                                            <Input type="textarea" placeholder="Comments" value={this.state.comments}
                                                   onChange={(e) => this.setField('comments', e.target.value)} style={{minHeight: 38}}/>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="3">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Fuel $</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.fuel_amount}</div>
                                        ) : (
                                            <Input type="number" step="0.01" min="0" value={this.state.fuel_amount}
                                                   onChange={(e) => this.setField('fuel_amount', e.target.value)}
                                                   placeholder="0.00"/>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="3">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Fuel L</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.fuel_litres}</div>
                                        ) : (
                                            <Input type="number" step="0.01" min="0" value={this.state.fuel_litres}
                                                   onChange={(e) => this.setField('fuel_litres', e.target.value)}
                                                   placeholder="0.0"/>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="3">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Def $</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.def_amount}</div>
                                        ) : (
                                            <Input type="number" step="0.01" min="0" value={this.state.def_amount}
                                                   onChange={(e) => this.setField('def_amount', e.target.value)}
                                                   placeholder="0.00"/>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="3">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Def L</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.def_litres}</div>
                                        ) : (
                                            <Input type="number" step="0.01" min="0" value={this.state.def_litres}
                                                   onChange={(e) => this.setField('def_litres', e.target.value)}
                                                   placeholder="0.0"/>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <table className="table table-sm bg-light">
                                    <thead className="thead-light" style={{fontSize: '0.8rem'}}>
                                    <tr>
                                        <th>Location</th>
                                        <th>Arr</th>
                                        <th>Dep</th>
                                        <th>Odo</th>
                                        <th>Cont #</th>
                                        <th>Chas #</th>
                                        <th>Delay</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {this.state.rows.map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{minWidth: 130}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.location}</span>
                                                ) : (
                                                    <Input type="text" value={row.location}
                                                           onChange={(e) => this.setRowField(idx, 'location', e.target.value)}
                                                           placeholder="Location"/>
                                                )}
                                            </td>
                                            <td style={{minWidth: 80}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.arrive}</span>
                                                ) : (
                                                    <Input
                                                        type="time"
                                                        step="60"
                                                        bsSize="sm"
                                                        style={{fontSize: '0.8rem', paddingLeft: 4, paddingRight: 4, width: '100%'}}
                                                        value={row.arrive}
                                                        onChange={(e) => this.setRowField(idx, 'arrive', e.target.value)}
                                                        onFocus={() => this.ensureTimeValue(idx, 'arrive')}
                                                    />
                                                )}
                                            </td>
                                            <td style={{minWidth: 80}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.depart}</span>
                                                ) : (
                                                    <Input
                                                        type="time"
                                                        step="60"
                                                        bsSize="sm"
                                                        style={{fontSize: '0.8rem', paddingLeft: 4, paddingRight: 4, width: '100%'}}
                                                        value={row.depart}
                                                        onChange={(e) => this.setRowField(idx, 'depart', e.target.value)}
                                                        onFocus={() => this.ensureTimeValue(idx, 'depart')}
                                                    />
                                                )}
                                            </td>
                                            <td style={{minWidth: 100}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.odometer}</span>
                                                ) : (
                                                    <Input type="number" value={row.odometer}
                                                           onChange={(e) => this.setRowField(idx, 'odometer', e.target.value)}
                                                           placeholder="km"/>
                                                )}
                                            </td>
                                            <td style={{minWidth: 110}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.container}</span>
                                                ) : (
                                                    <Input type="text" value={row.container}
                                                           onChange={(e) => this.setRowField(idx, 'container', e.target.value)}
                                                           placeholder="Cont #"/>
                                                )}
                                            </td>
                                            <td style={{minWidth: 100}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.chassis}</span>
                                                ) : (
                                                    <Input type="text" value={row.chassis}
                                                           onChange={(e) => this.setRowField(idx, 'chassis', e.target.value)}
                                                           placeholder="Chas #"/>
                                                )}
                                            </td>
                                            <td style={{minWidth: 70}}>
                                                {this.state.is_submitted ? (
                                                    <span>{row.delay}</span>
                                                ) : (
                                                    <Input type="number" min="0" value={row.delay}
                                                           onChange={(e) => this.setRowField(idx, 'delay', e.target.value)}
                                                           placeholder="Min."/>
                                                )}
                                            </td>
                                            <td className="text-right" style={{minWidth: 60}}>
                                                {!this.state.is_submitted && (
                                                    <Button color="danger" size="sm" onClick={() => this.removeRow(idx)}>
                                                        <i className="fa fa-trash"/>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                            {!this.state.is_submitted && (
                                <div className="d-flex justify-content-center">
                                    <Button color="info" size="sm" onClick={this.addRow}>+ Add Row</Button>
                                </div>
                            )}

                            {/* Attachments */}
                            <Row className="mt-3">
                                <Col sm="12">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Attachments</label>
                                        {!this.state.is_submitted && this.state.trip_id && (
                                            <div className="mb-2">
                                                <input
                                                    type="file"
                                                    accept="application/pdf,image/jpeg,image/jpg"
                                                    multiple
                                                    onChange={this.handleAttachmentChange}
                                                />
                                            </div>
                                        )}
                                        {this.state.attachments && this.state.attachments.length > 0 && (
                                            <ul className="list-unstyled mb-0">
                                                {this.state.attachments.map((a) => (
                                                    <li key={a.attachment_id}
                                                        className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                                        <a href={`/trip_attachments/${a.file_name}`} target="_blank"
                                                           rel="noopener noreferrer">
                                                            {a.original_name || a.file_name}
                                                        </a>
                                                        {!this.state.is_submitted && (
                                                            <Button color="danger" size="sm"
                                                                    onClick={() => this.deleteAttachment(a.attachment_id)}>
                                                                <i className="fa fa-trash"/>
                                                            </Button>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {(!this.state.attachments || this.state.attachments.length === 0) && (
                                            <div className="text-muted">No attachments yet.</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row className="mt-2">
                                <Col sm="12" md="6">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Distance Travelled (km)</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.total_distance}</div>
                                        ) : (
                                            <Input type="number" step="0.1" min="0" value={this.state.total_distance}
                                                   onChange={(e) => this.setField('total_distance', e.target.value)}
                                                   placeholder="0.0"/>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col sm="12" md="6">
                                    <FormGroup className="bg-light">
                                        <label className="mb-0 font-weight-bold">Total Hours Worked</label>
                                        {this.state.is_submitted ? (
                                            <div>{this.state.total_hours}</div>
                                        ) : (
                                            <Input type="number" step="0.1" min="0" value={this.state.total_hours}
                                                   onChange={(e) => this.setField('total_hours', e.target.value)}
                                                   placeholder="0.0"/>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            {!this.state.is_submitted && (
                                <div className="d-flex justify-content-end mt-3">
                                    <Button color="primary" onClick={this.submitTrip}>Submit Trip Sheet</Button>
                                </div>
                            )}

                            {this.state.is_submitted && (
                                <div className="mt-3 text-center">
                                    <div className="alert alert-success p-1"><i className="fa fa-check fa-lg mr-2"></i> Trip sheet has been submitted.</div>
                                    <Button color="info" className="mt-2" onClick={this.startNewTrip}>
                                        Create New Trip Sheet
                                    </Button>
                                </div>
                            )}
                        </form>
                    </CardBody>
                </Card>
            </ContentWrapper>
        );
    }
}

export default TripsCreate;
